import * as React from 'react';
import PropTypes from 'prop-types';
import Skeleton from 'react-loading-skeleton';
import ActionButton from '../shared/action_button';
import Avatar from '../avatar';
import IconWarningAlt from '../icons/icon_warning_alt';
import useTableContext from '../table/use_table_context';
import TableCell from '../table_cell';
import TableRow from '../table_row';
import TableSelectionCell from '../table_selection_cell';
import useStyles from './use_styles';
import Scrollbar from '../scrollbar';
import { jsx as ___EmotionJSX } from "@emotion/core";
var defaultComponent = 'tbody';
var TableBody = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  var {
    component = defaultComponent
  } = _ref;
  var [openedAction, setOpenedAction] = React.useState(undefined);
  var {
    columnResize,
    columns,
    customNoItems,
    data: dataProp,
    hasActions,
    i18n,
    indexColumnId,
    isSinglePage,
    loading,
    loadingDataLength,
    onActionButtonToggle,
    onSelect,
    rowSelector,
    theme
  } = useTableContext();
  var columnIds = columns.map(c => c.id);
  var style = useStyles({
    isSinglePage,
    theme
  });
  var emptyData = [];
  for (var i = 0; i < loadingDataLength; i += 1) {
    emptyData.push({});
  }
  var data = loading ? emptyData : dataProp;
  var handleActionClick = React.useCallback(rowID => {
    onActionButtonToggle();
    setOpenedAction(rowID);
  }, [onActionButtonToggle]);
  var handleActionClose = React.useCallback(() => {
    onActionButtonToggle(false);
    setOpenedAction(undefined);
  }, [onActionButtonToggle]);
  return ___EmotionJSX(Scrollbar, {
    component: component,
    customStyle: style.root,
    ref: ref,
    role: component === defaultComponent ? null : 'rowgroup',
    roundMargin: "0px 10px",
    sharpMargin: "0px 10px"
  }, data.length === 0 ? ___EmotionJSX(TableRow, {
    css: style.noItems.row
  }, ___EmotionJSX(TableCell, {
    colSpan: columns.length + (rowSelector ? 1 : 0),
    css: style.noItems.cell,
    value: customNoItems || ___EmotionJSX("div", {
      css: style.noItems.div
    }, ___EmotionJSX(Avatar, {
      css: style.noItems.avatar,
      icon: ___EmotionJSX(IconWarningAlt, null)
    }), ___EmotionJSX("span", null, i18n.noItems))
  })) : data.map((row, i) => {
    var _row$rowConfig, _row$rowConfig2, _row$rowConfig3, _row$rowConfig4, _row$rowConfig5, _row$rowConfig6;
    var action = (_row$rowConfig = row.rowConfig) === null || _row$rowConfig === void 0 ? void 0 : _row$rowConfig.action;
    var selection = (_row$rowConfig2 = row.rowConfig) === null || _row$rowConfig2 === void 0 ? void 0 : _row$rowConfig2.rowSelector;
    var isActionOpened = openedAction === i;
    var rowId = indexColumnId && ['string', 'number'].includes(typeof row[indexColumnId]) ? row[indexColumnId] : undefined;
    return ___EmotionJSX(TableRow, {
      key: "table-row-".concat(i),
      selected: ((_row$rowConfig3 = row.rowConfig) === null || _row$rowConfig3 === void 0 ? void 0 : _row$rowConfig3.selected) && !!rowSelector
    }, rowSelector && (loading ? ___EmotionJSX("td", null, rowSelector === 'avatar' ? ___EmotionJSX(Skeleton, {
      circle: true,
      height: "36px",
      style: style.avatarSkeleton,
      width: "36px"
    }) : ___EmotionJSX(Skeleton, {
      height: "24px",
      style: style.multiSelectSkeleton,
      width: "24px"
    })) : ___EmotionJSX(TableSelectionCell, {
      label: rowId === null || rowId === void 0 ? void 0 : rowId.toString(),
      disabled: (_row$rowConfig4 = row.rowConfig) === null || _row$rowConfig4 === void 0 ? void 0 : _row$rowConfig4.disabled,
      tooltipContent: (_row$rowConfig5 = row.rowConfig) === null || _row$rowConfig5 === void 0 || (_row$rowConfig5 = _row$rowConfig5.rowSelector) === null || _row$rowConfig5 === void 0 ? void 0 : _row$rowConfig5.tooltipContent,
      id: rowId,
      onSelect: onSelect,
      selected: (_row$rowConfig6 = row.rowConfig) === null || _row$rowConfig6 === void 0 ? void 0 : _row$rowConfig6.selected,
      type: rowSelector,
      avatar: ___EmotionJSX(Avatar, {
        icon: selection === null || selection === void 0 ? void 0 : selection.icon,
        image: selection === null || selection === void 0 ? void 0 : selection.image,
        label: selection === null || selection === void 0 ? void 0 : selection.label
      })
    })), columnIds.map((value, index) => {
      var _row$rowConfig7;
      return ___EmotionJSX(TableCell, {
        id: value,
        key: "table-body-cell-".concat(value, "-").concat(index),
        noWrap: (_row$rowConfig7 = row.rowConfig) === null || _row$rowConfig7 === void 0 ? void 0 : _row$rowConfig7.noWrap,
        value: row[value]
      });
    }), columnResize && ___EmotionJSX(TableCell, null), hasActions && action && ___EmotionJSX("td", {
      css: style.actionCell
    }, /*#__PURE__*/React.cloneElement(action, {
      onClick: (event, option) => action.props.onClick(event, option, i),
      onClose: handleActionClose,
      open: isActionOpened,
      portal: true,
      useJsPositioning: true
    }, ___EmotionJSX(ActionButton, {
      "aria-label": i18n.actionButton,
      customStyle: style.actionButton(isActionOpened)
      // eslint-disable-next-line react/jsx-no-bind
      ,
      onClick: () => handleActionClick(i)
    }))), hasActions && !action && ___EmotionJSX("td", null));
  }));
});
TableBody.propTypes = {
  component: PropTypes.elementType
};
TableBody.defaultProps = {
  component: defaultComponent
};
TableBody.displayName = 'TableBody';
export default TableBody;